import React from "react";
import { useTheme, styled } from "@mui/material/styles";
import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  CssBaseline,
  Typography,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  alpha,
  Tooltip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Loader from "../../components/Loader";
import ConfirmModal from "../Common/CommonModal";
import { removeAuthObjects } from "../../utils";
import { Link } from "react-router-dom";

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import AlbumIcon from "@mui/icons-material/Album";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PeopleIcon from "@mui/icons-material/People";
import Logo from "../../assets/img/logo.png";

const drawerWidth = 280;
const collapsedWidth = 72;

// Styled Components
const StyledAppBar = styled(AppBar)(({ theme, open }) => ({
  background: '#0F172A',
  borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
  boxShadow: 'none',
  borderRadius: 0,
  zIndex: theme.zIndex.drawer + 1,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
  }),
  ...(!open && {
    marginLeft: collapsedWidth,
    width: `calc(100% - ${collapsedWidth}px)`,
  }),
}));

const StyledToolbar = styled(Toolbar)({
  padding: '0 24px',
  minHeight: '70px !important',
  height: 70,
  display: 'flex',
  justifyContent: 'space-between',
});

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedWidth,
    background: '#0F172A',
    borderRight: `1px solid ${alpha('#fff', 0.06)}`,
    borderRadius: 0,
    overflowX: 'hidden',
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

const NavSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 1.5),
  flex: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha('#fff', 0.1),
    borderRadius: 2,
  },
}));

const NavItem = styled(ListItem)(({ theme, active }) => ({
  borderRadius: 8,
  marginBottom: 4,
  padding: '12px 16px',
  color: active ? '#fff' : alpha('#fff', 0.7),
  background: active
    ? `linear-gradient(135deg, ${alpha('#12B4A1', 0.2)} 0%, ${alpha('#12B4A1', 0.1)} 100%)`
    : 'transparent',
  border: active ? `1px solid ${alpha('#12B4A1', 0.3)}` : '1px solid transparent',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    background: active
      ? `linear-gradient(135deg, ${alpha('#12B4A1', 0.25)} 0%, ${alpha('#12B4A1', 0.15)} 100%)`
      : alpha('#fff', 0.05),
    color: '#fff',
    transform: 'translateX(4px)',
  },
}));

const NavIcon = styled(ListItemIcon)(({ active }) => ({
  minWidth: 40,
  color: active ? '#12B4A1' : alpha('#fff', 0.5),
  transition: 'color 0.2s ease',
}));

const NavText = styled(ListItemText)(({ theme }) => ({
  '& .MuiTypography-root': {
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '0.01em',
  },
}));

const HeaderTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '1.25rem',
  color: '#fff',
  letterSpacing: '-0.01em',
});

const WelcomeText = styled(Typography)({
  fontSize: '0.875rem',
  color: alpha('#fff', 0.7),
  fontWeight: 500,
});

const LogoutItem = styled(ListItem)(({ theme }) => ({
  borderRadius: 12,
  marginTop: 'auto',
  padding: '12px 16px',
  color: alpha('#FF6B6B', 0.9),
  background: alpha('#FF6B6B', 0.08),
  border: `1px solid ${alpha('#FF6B6B', 0.15)}`,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    background: alpha('#FF6B6B', 0.15),
    color: '#FF6B6B',
  },
}));

const ActiveIndicator = styled(Box)(({ active }) => ({
  position: 'absolute',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 3,
  height: active ? 24 : 0,
  background: 'linear-gradient(180deg, #12B4A1 0%, #0596B3 100%)',
  borderRadius: '0 4px 4px 0',
  transition: 'height 0.2s ease',
}));

const initialListItems = [
  {
    label: "Dashboard",
    icon: <DashboardIcon />,
    path: `/`,
  },
  {
    label: "Audio Catalog",
    icon: <AlbumIcon />,
    path: `/audio-catalog`,
  },
  {
    label: "Video Catalog",
    icon: <VideoLibraryIcon />,
    path: `/video-catalog`,
  },
  {
    label: "Sub Labels",
    icon: <PeopleIcon />,
    path: `/sub-labels`,
  },
  {
    label: "FAQ",
    icon: <HelpOutlineIcon />,
    path: `https://fronicmedia.com/knowledge-base/`,
    external: true,
  },
];

export default function MainContainer(props) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const [loader, setLoader] = React.useState(false);
  const [confirmOpen, setConfimOpen] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      if (document.body.clientWidth < 900) {
        if (open) setOpen(false);
      } else {
        if (!open) setOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [open]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const header = () => {
    const { path } = props.match;
    switch (path) {
      case "/":
        return "Dashboard";
      case "/news":
        return "News";
      case "/audio-catalog":
        return "Audio Catalog";
      case "/video-catalog":
        return "Video Catalog";
      case "/sub-labels":
        return "Sub Labels";
      case "/user_details":
        return "User Details";
      case "/report_requests":
        return "Report Requests";
      case "/tickets":
        return "Tickets";
      case "/payout":
        return "Requested Payouts";
      case "/notifications":
        return "Notifications";
      case "/ugc_claims":
        return "UGC Claims";
      case "/profile_linking":
        return "Profile Linking";
      case "/playlist_pitching":
        return "Playlist Pitching";
      default: {
        return "";
      }
    }
  };

  const handleConfirmClickOpen = () => {
    setConfimOpen(true);
  };

  const handleConfirmClose = () => {
    setConfimOpen(false);
  };

  const onConfirmLogout = () => {
    onSetLoader(true);
    removeAuthObjects();
    setTimeout(() => {
      onSetLoader(false);
      return props.history.push("/login");
    }, 1000);
  };

  const onSetLoader = (action) => {
    setLoader(action);
  };

  const LoaderStyle = {
    height: "100%",
    overflow: "hidden",
  };

  const isActive = (path) => props.location.pathname === path;

  return (
    <Loader
      className="main-loader"
      style={loader ? LoaderStyle : null}
      show={loader}
    >
      <Box sx={{ display: "flex", minHeight: "100vh", background: '#0F172A' }}>
        <CssBaseline />

        {/* AppBar */}
        <StyledAppBar position="fixed" open={open}>
          <StyledToolbar>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HeaderTitle>{header()}</HeaderTitle>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WelcomeText sx={{ display: { xs: 'none', sm: 'block' } }}>Welcome, Label Team</WelcomeText>
            </Box>
          </StyledToolbar>
        </StyledAppBar>

        {/* Sidebar */}
        <StyledDrawer variant="permanent" open={open}>
          <Box
            sx={{
              px: open ? 2 : 1,
              borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: open ? 'space-between' : 'center',
              height: 70,
            }}
          >
            {open && (
              <img
                src={Logo}
                alt="Fronic Media"
                style={{
                  height: 40,
                  objectFit: 'contain',
                }}
              />
            )}
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: alpha('#fff', 0.7),
                p: 1,
                borderRadius: 2,
                '&:hover': {
                  background: alpha('#fff', 0.1),
                  color: '#fff',
                },
              }}
            >
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
          <NavSection>
            <List sx={{ p: 0 }}>
              {initialListItems.map((item) => (
                item.external ? (
                  <a
                    key={item.label}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <Tooltip title={!open ? item.label : ''} placement="right">
                      <NavItem active={0}>
                        <ActiveIndicator active={0} />
                        <NavIcon active={0}>
                          {item.icon}
                        </NavIcon>
                        {open && <NavText primary={item.label} />}
                      </NavItem>
                    </Tooltip>
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.path}
                    style={{ textDecoration: 'none' }}
                  >
                    <Tooltip title={!open ? item.label : ''} placement="right">
                      <NavItem active={isActive(item.path) ? 1 : 0}>
                        <ActiveIndicator active={isActive(item.path) ? 1 : 0} />
                        <NavIcon active={isActive(item.path) ? 1 : 0}>
                          {item.icon}
                        </NavIcon>
                        {open && <NavText primary={item.label} />}
                      </NavItem>
                    </Tooltip>
                  </Link>
                )
              ))}
            </List>
          </NavSection>

          {/* Logout Button */}
          <Box sx={{ p: 1.5 }}>
            <Tooltip title={!open ? "Logout" : ''} placement="right">
              <LogoutItem onClick={handleConfirmClickOpen}>
                <NavIcon>
                  <MeetingRoomIcon sx={{ color: alpha('#FF6B6B', 0.9) }} />
                </NavIcon>
                {open && (
                  <ListItemText
                    primary="Logout"
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      }
                    }}
                  />
                )}
              </LogoutItem>
            </Tooltip>
          </Box>
        </StyledDrawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            minHeight: '100vh',
            background: '#1E293B',
            transition: 'margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Box sx={{ height: 70 }} />
          {React.cloneElement(props.children, { setLoader: onSetLoader })}
        </Box>

        {/* Modals */}
        <ConfirmModal
          message={`Are you sure you want to Logout?`}
          open={confirmOpen}
          title={`Confirm Logout`}
          onSubmitConfirm={onConfirmLogout}
          handleClose={handleConfirmClose}
        />

      </Box>
    </Loader>
  );
}
